import { useState, type ReactNode, type SyntheticEvent } from 'react';
import { Link } from 'react-router';
import { useTranslations } from 'use-intl';
import { Explain } from '@/ui/Explain';

/**
 * A titled region. Deliberately not a card: one flat surface with a ruled head,
 * so a page of panels reads as sections of one document rather than a grid of
 * tiles competing for attention.
 *
 * A panel can also be closed. The match screen is seven of these stacked —
 * twelve and a half screens of scrolling, most of it about a part of the week
 * that has not happened yet — and a coach preparing on Thursday should not have
 * to scroll past what he will write on Saturday. Closed, a panel still says its
 * name, its count and its summary, so what is behind it is never a mystery.
 */
export function Panel({
  title,
  count,
  action,
  explain,
  collapsible = false,
  defaultOpen = true,
  storeKey,
  summary,
  children,
}: {
  /** Omit on a screen whose page header already says this. */
  title?: string;
  count?: ReactNode;
  action?: ReactNode;
  /** Knowledge-layer term, shown as a "?" beside the heading. */
  explain?: string;
  /** Lets the coach close it. Use where a page stacks several. */
  collapsible?: boolean;
  /**
   * Whether it starts open.
   *
   * Decide this from the coach's own data rather than a guess — the match
   * screen opens the part of the week he is actually in. A panel that opens on
   * something empty is the clutter this exists to remove.
   */
  defaultOpen?: boolean;
  /** Remembers the coach's own choice for this panel, per browser. */
  storeKey?: string;
  /** One line shown when it is closed, so closing loses nothing. */
  summary?: ReactNode;
  children: ReactNode;
}) {
  const open = useRemembered(storeKey, defaultOpen, collapsible);

  const head = (
    <>
      {/*
        A title is optional, because a screen that is one panel should not say
        its own name twice. Ten of twenty-two did: "Decision log" as the page,
        "Decision log" again in the box directly under it — which is the first
        thing that makes a product look like a form someone generated.

        Left out rather than hidden: the page's own `h1` is the heading for
        this region, so adding a silent `h2` saying the same words would give a
        screen reader the stutter we just took off the screen.
      */}
      {title ? (
        <h2 className="font-condensed text-subhead font-semibold tracking-tight">{title}</h2>
      ) : null}
      {explain ? <Explain term={explain} /> : null}
      {count !== undefined ? (
        <span className="tabular text-meta text-ink-faint">{count}</span>
      ) : null}
      {summary !== undefined ? (
        <span className="min-w-0 flex-1 truncate text-meta text-ink-faint group-open:hidden">
          {summary}
        </span>
      ) : null}
      {action ? <div className="ms-auto">{action}</div> : null}
    </>
  );

  // Nothing to say, so no bar saying it. A panel whose page header already
  // carries its name, its count and its "?" was left rendering an empty ruled
  // strip — or worse, one holding a single grey number and nothing else.
  const hasHead =
    Boolean(title) || Boolean(action) || Boolean(explain) ||
    count !== undefined || summary !== undefined;

  if (!collapsible) {
    return (
      <section className="overflow-hidden rounded-(--radius-panel) border border-line bg-surface shadow-(--shadow-panel)">
        {hasHead ? (
          <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-line px-4 py-3">
            {head}
          </header>
        ) : null}
        {children}
      </section>
    );
  }

  return (
    <details
      open={open.value}
      onToggle={open.onToggle}
      className="group overflow-hidden rounded-(--radius-panel) border border-line bg-surface shadow-(--shadow-panel)"
    >
      {/* `<details>` rather than state: keyboard and screen-reader behaviour
          for free, and it works before the JavaScript that remembers it. */}
      <summary className="flex min-h-14 cursor-pointer list-none flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 group-open:border-b group-open:border-line">
        {head}
        <span
          aria-hidden="true"
          className="ms-auto text-ink-faint transition-transform group-open:rotate-90"
        >
          ›
        </span>
      </summary>
      {children}
    </details>
  );
}

/**
 * The coach's own open/closed choice, kept per browser.
 *
 * Seeded from `defaultOpen` so the first visit opens on whatever the data says
 * matters; after that his choice wins, because a panel that reopens every time
 * he closes it is a panel that is arguing with him.
 */
function useRemembered(storeKey: string | undefined, defaultOpen: boolean, enabled: boolean) {
  // Read during the first render, not in an effect.
  //
  // `<details>` is only half controlled: React writes the `open` attribute when
  // its own value changes, so a state change one tick after mount can leave the
  // DOM where it already was. Setting it from an effect restored some panels
  // and not others, which is exactly the shape of that bug. Storage is
  // synchronous, so there is no reason to wait — and this way nothing flashes
  // open before closing either.
  const [value, setValue] = useState(() => {
    if (!enabled || !storeKey) return defaultOpen;
    try {
      const stored = window.localStorage.getItem(`mpp.panel.${storeKey}`);
      return stored === '1' ? true : stored === '0' ? false : defaultOpen;
    } catch {
      return defaultOpen; // a private window is still a usable window
    }
  });

  return {
    value,
    onToggle: (e: SyntheticEvent<HTMLDetailsElement>) => {
      const next = e.currentTarget.open;
      setValue(next);
      if (!storeKey) return;
      try {
        window.localStorage.setItem(`mpp.panel.${storeKey}`, next ? '1' : '0');
      } catch {
        /* forgetting the choice is survivable */
      }
    },
  };
}

/**
 * "Go to the screen that can act on this."
 *
 * Every panel on the dashboard ends in one of these, which is the whole reason
 * a dashboard is worth opening. They were `text-meta` links about 17px tall —
 * accurate as text and not a target. The visual weight is unchanged; the box
 * around it is now 44px, with the vertical padding pulled back out of the
 * header so the rule above does not move.
 */
export function PanelLink({
  to,
  children,
}: {
  to: string;
  children: ReactNode;
}) {
  return (
    <Link
      to={to}
      className="-my-3 inline-flex min-h-11 min-w-11 items-center justify-center px-2 text-meta text-ink-muted underline decoration-line-strong underline-offset-2 transition-colors hover:text-ink hover:decoration-ink"
    >
      {children}
    </Link>
  );
}

/**
 * The shape of what is coming, while it comes.
 *
 * A panel that returns `null` until its data arrives is a hole in the page: on
 * a slow connection at the side of a pitch the screen looks broken rather than
 * busy, and the content then appears and shoves everything below it down.
 *
 * Grey blocks rather than a spinner, because the point is the layout. The pulse
 * is `animate-pulse`, which the global `prefers-reduced-motion` rule already
 * clamps to a stop for anyone who asked for that.
 */
export function Skeleton({ lines = 3, className = '' }: { lines?: number; className?: string }) {
  return (
    <div className={`grid gap-2 p-4 ${className}`} aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <div
          key={i}
          className="h-4 animate-pulse rounded-(--radius-control) bg-surface-sunken"
          // Varied widths, so it reads as text rather than as a table.
          style={{ width: `${[92, 68, 80, 55, 74][i % 5]}%` }}
        />
      ))}
    </div>
  );
}

export function PageHeader({
  title,
  lead,
  action,
  count,
  explain,
  help,
}: {
  title: string;
  lead?: string;
  action?: ReactNode;
  /**
   * How many there are, said once beside the name of the thing.
   *
   * It used to sit in the panel below, which on a screen that is one list left
   * the panel's head holding a lone grey figure and nothing else.
   */
  count?: ReactNode;
  /** Knowledge-layer term for the screen itself, shown as a "?" by the title. */
  explain?: string;
  /**
   * Path to the Guide page that explains this screen.
   *
   * The Guide has thirteen pages and, until now, exactly one link into it
   * anywhere in the product — inside a collapsed menu. A manual nobody can
   * find from the thing it describes is a manual nobody reads.
   */
  help?: string;
}) {
  const t = useTranslations();
  return (
    <header className="mb-6">
      {/*
        The action sits on the title's line, not at the bottom of the block.

        Aligned to the end of the whole header it came to rest level with the
        "What is this for?" link — a primary button floating alone in the
        gutter, a long way from the thing it acts on and from anything it is
        aligned to. Beside the title it reads as the screen's action, which is
        what it is.
      */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-3">
        <h1 className="min-w-0 font-condensed text-title font-semibold leading-tight tracking-tight">
          {title}
        </h1>
        {count !== undefined ? (
          <span className="tabular text-meta text-ink-faint">{count}</span>
        ) : null}
        {explain ? <Explain term={explain} /> : null}
        {action ? <div className="ms-auto shrink-0">{action}</div> : null}
      </div>
      {lead ? (
        <p className="mt-1 max-w-[60ch] text-ink-muted">
          {lead}
          {help ? (
            <>
              {' '}
              <Link
                to={help}
                className="inline-flex min-h-11 items-center text-meta text-ink-muted underline decoration-line-strong underline-offset-2 transition-colors hover:text-ink hover:decoration-ink"
              >
                {t('common.whatIsThisFor')}
              </Link>
            </>
          ) : null}
        </p>
      ) : null}
    </header>
  );
}

export function EmptyState({
  title,
  body,
  action,
}: {
  title: string;
  body: string;
  action?: ReactNode;
}) {
  return (
    <div className="px-4 py-12 text-center">
      <p className="font-condensed text-subhead font-semibold">{title}</p>
      <p className="mx-auto mt-1 max-w-[46ch] text-ink-muted">{body}</p>
      {action ? <div className="mt-5 flex justify-center">{action}</div> : null}
    </div>
  );
}

/** Non-blocking message. Errors say what happened and what to do about it. */
export function Notice({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'risk' | 'ok';
  children: ReactNode;
}) {
  const tones = {
    info: 'border-line bg-surface-sunken text-ink-muted',
    risk: 'border-risk/30 bg-risk/5 text-risk',
    ok: 'border-ok/30 bg-ok/5 text-ok',
  } as const;
  return (
    <p role={tone === 'risk' ? 'alert' : undefined} className={`rounded-(--radius-control) border px-3 py-2 text-meta ${tones[tone]}`}>
      {children}
    </p>
  );
}
